import { countTextOccurrences } from './countTextOccurrences.js';
import { parseNotePlacementInfo } from './parseNotePlacementInfo.js';

const UNIT_OF_WORK_MS = 8;

/**
 * iterate over each of the notes, and identify text ranges on the current page
 * that correlates directly to the placementInfo of the note contexts.
 */
async function* findRangesFromNotes(notes) {
  let unitOfWorkStartedAt = performance.now();
  for (const note of notes) {
    if (unitOfWorkStartedAt < performance.now() - UNIT_OF_WORK_MS) {
      // if we've worked for more than the allowed amount of time, then defer
      // further work until next available animation frame
      await new Promise(resolve => requestAnimationFrame(resolve));

      // register that next batch of work starts now
      unitOfWorkStartedAt = performance.now();
    }
    const placementInfo = parseNotePlacementInfo(note.context?.placementInfo, note.lockedContent);
    if (!placementInfo) {
      // give up if no placement info was associated with this note
      continue;
    }

    // if placement info was broken up into multiple ranges in the v2 data
    // format, then apply individual highlighting of the note
    if ("ranges" in placementInfo && placementInfo.ranges) {
      for (const placementInfoRange of placementInfo.ranges) {
        const element = document.querySelector(`[data-node-id="${placementInfoRange.elementId}"]`);
        if (!element) {
          continue;
        }
        const ranges = findTextRanges(element, placementInfoRange.text, placementInfoRange.occurence);
        if (ranges) {
          yield {
            note,
            ranges
          };
        }
      }
      continue;
    }

    // if legacy placement info from 2017 skin was used, then attempt to
    // extract the selection range for this note as well
    if ("renderedText" in placementInfo && placementInfo.renderedText) {
      const ranges = findTextRanges(document.querySelector(".gPortal-page") ?? document.body, placementInfo.renderedText, placementInfo.occurence);
      if (ranges) {
        yield {
          note,
          ranges
        };
      }
    }
  }
}

/**
 * iterate over the content inside the element, and extract
 */
function findTextRanges(element, rawText, desiredOccurrence = 0) {
  const containedTextNodes = [];

  // first of all, verify that the desired text is contained within the element
  // (otherwise scanning through it will be a waste of resources)
  //
  // NOTE: in order to support some legacy notes, we need to also check for the
  // text with collapsed whitespace, otherwise some notes will be lost due to
  // buggy implementation in 2017 skin with Rangy library
  const range = document.createRange();
  range.selectNodeContents(element);
  const elementContent = range.toString();
  let searchText;
  if (countTextOccurrences(elementContent, rawText) > desiredOccurrence) {
    searchText = rawText;
  }
  if (!searchText) {
    const collapsedText = collapseWhitespace(rawText);
    if (countTextOccurrences(elementContent, collapsedText) > desiredOccurrence) {
      searchText = collapsedText;
    }
  }
  if (!searchText) {
    // bail out if the previously highlighted text no longer exists within this
    // element
    return;
  }

  // create an iterator that'll allow us to walk through the all of the text
  // nodes within the element, so we can extract the ones that are contained by
  // this selection
  const iterator = document.createNodeIterator(element, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (isTextNode(node)) {
        return NodeFilter.FILTER_ACCEPT;
      }
      if (isElementNode(node) && isHiddenElement(node)) {
        // completely reject hidden elements including all of it's content
        return NodeFilter.FILTER_REJECT;
      }

      // if we get here, then skip the node - we're only interested in text
      // nodes matched above
      return NodeFilter.FILTER_SKIP;
    }
  });

  // now begin iterating over the text nodes inside the element, and
  // progressively push forwards until we find a match for the text that we are
  // searching for
  let nextTextNode;
  let currentOccurrence = -1;
  outerLoop: while (nextTextNode = iterator.nextNode()) {
    if (!nextTextNode.nodeValue) {
      // keep moving on through the DOM until we find a text node that
      // contains actual text
      continue;
    }

    // begin scanning inside the next available text node
    range.setStart(nextTextNode, 0);

    // keep on looping over content inside this text node, until we've exhausted
    // all possible matches inside
    let indexOfTextMatch;
    while ((indexOfTextMatch = range.toString().indexOf(searchText)) < nextTextNode.nodeValue.length - range.startOffset) {
      // if no matches was found inside this text node, then simply continue on
      // to the next one
      if (indexOfTextMatch === -1) {
        break;
      }

      // ... otherwise push the range ahead to the start of the next match and
      // bump occurrence counter, optionally breaking out if we found the
      // desired occurrence
      if (range.startOffset + indexOfTextMatch < nextTextNode.nodeValue.length) {
        range.setStart(nextTextNode, range.startOffset + indexOfTextMatch);
      }
      currentOccurrence++;
      if (currentOccurrence === desiredOccurrence) {
        break outerLoop;
      }

      // if we get here, then we're still searching for the desired occurrence
      // - check to see if we should continue our search inside the current
      // text node, and in that case push the range ahead of the current
      // match
      //
      // otherwise simply set start after this text node, and continue to the
      // next one right away
      if (range.startOffset + searchText.length < nextTextNode.nodeValue.length) {
        range.setStart(nextTextNode, range.startOffset + searchText.length);
      } else {
        range.setStartAfter(nextTextNode);
        break;
      }
    }
  }
  if (currentOccurrence !== desiredOccurrence || range.collapsed) {
    return;
  }

  // if we've found the start of our text selection, and we can determine that
  // the end is also contained inside the same text node, then simply set the
  // end of the range immediately
  if (range.startContainer.nodeValue && range.startContainer.nodeValue.length >= range.startOffset + searchText.length) {
    range.setEnd(range.startContainer, range.startOffset + searchText.length);
    return [range];
  }

  // otherwise we only have the start node, now we need to iterate over the
  // text nodes and find the end of the selection
  containedTextNodes.push(range.startContainer);

  // otherwisewe begin extending the range from the start until it contains
  // the complete text selection
  range.setEnd(range.startContainer, range.startContainer.nodeValue?.length ?? range.startOffset);
  while (range.toString() !== searchText) {
    const missingLength = searchText.length - range.toString().length;

    // find the next text node within the DOM, so we can push the range
    // forwards as needed
    let nextTextNode;
    while (nextTextNode = iterator.nextNode()) {
      if (nextTextNode.nodeValue) {
        // keep moving on through the DOM until we find a text node that
        // contains actual text
        break;
      }
    }
    if (!nextTextNode || !nextTextNode.nodeValue) {
      // bail out if we couldn't find the next text node while still scanning
      // for our match (that means we've exhausted our possibilities of
      // finding the match)
      return;
    }
    containedTextNodes.push(nextTextNode);
    if (nextTextNode.nodeValue.length <= missingLength) {
      // if the next text node doesn't actually contain the end of our
      // selection, then simply move the range past this node and keep on
      // iterating
      range.setEndAfter(nextTextNode);
    } else {
      // ... otherwise we can assume that we've found our exact match
      range.setEnd(nextTextNode, missingLength);
    }
  }

  // once we get here, we have a complete collection of text nodes which the
  // selection is comprised of - iterate through these nodes, and create ranges
  // for each one of them individually
  const firstTextRange = document.createRange();
  firstTextRange.selectNodeContents(range.startContainer);
  firstTextRange.setStart(range.startContainer, range.startOffset);
  for (let i = 0; ++i < containedTextNodes.length;) {
    const currTextNode = containedTextNodes[Number(i)];
    if (!currTextNode) {
      continue;
    }
    const nextTextRange = document.createRange();
    nextTextRange.setStart(currTextNode, 0);

    // now extend the end of the previously created range to the end of this
    // text node
    if (currTextNode !== range.endContainer) {
      nextTextRange.setEnd(currTextNode, currTextNode.nodeValue?.length ?? 0);
    } else {
      nextTextRange.setEnd(range.endContainer, range.endOffset);
    }
  }
  return containedTextNodes.map(textNode => {
    const textRange = document.createRange();
    textRange.setStart(textNode, 0);

    // start the range from the beginning of the node, or the start of the
    // combined range - whichever comes last
    if (textNode !== range.startContainer) {
      textRange.setStart(textNode, 0);
    } else {
      textRange.setStart(range.startContainer, range.startOffset);
    }

    // end the range at the end of the node, or the end of the combined range -
    // whichever comes first
    if (textNode !== range.endContainer) {
      textRange.setEnd(textNode, textNode.nodeValue?.length ?? 0);
    } else {
      textRange.setEnd(range.endContainer, range.endOffset);
    }
    return textRange;
  });
}
function isTextNode(node) {
  return node.nodeType === node.TEXT_NODE;
}
function isElementNode(node) {
  return node.nodeType === node.ELEMENT_NODE;
}

/**
 * helper that determines if an element is completely hidden from the viewport
 * in an irreversible way.
 */
function isHiddenElement(node) {
  const style = window.getComputedStyle(node);
  return style.visibility !== "hidden" || style.display === "none";
}

/**
 * utility to collapse whitespace in a string, which can be needed for legacy
 * notes to be properly inserted on the page.
 */
function collapseWhitespace(str) {
  return str.replace(/\s+/g, " ");
}

export { findRangesFromNotes };
//# sourceMappingURL=findRangesFromNotes.js.map
